import React, { useState } from 'react'
import { CheckCircle2, XCircle, CheckCheck } from 'lucide-react'
import KaTeXRenderer from '../../../components/common/KaTeXRenderer'
import { resultApi } from '../api/lessonBuilderApi'
import { celebrateCorrect, WRONG_ENCOURAGE } from '../../../components/interactive/shared/effects/feedbackEffects'
import { LessonStatsService } from '../../../services/stats/LessonStatsService'
import type { QuizCard } from '../types/LessonDoc'

interface Props {
  // card chỉ cần {id, title?, questions} — dùng được cả cho khối 'quiz' trong rich content
  card: Pick<QuizCard, 'id' | 'title' | 'questions'>
  lessonId?: string
  lessonSlug?: string
}

const norm = (s: any) => String(s ?? '').trim().toLowerCase()

// Trình làm quiz tương tác: KHÔNG có nút Nộp bài — mỗi câu có nút "Kiểm tra đáp án"
// chấm ngay Đúng/Sai (pháo giấy chúc mừng / nhắc xem lại kiến thức).
// Kiểm tra đủ tất cả các câu → tự tổng kết điểm + lưu kết quả về backend.
const QuizPlayer: React.FC<Props> = ({ card, lessonId, lessonSlug }) => {
  const questions = card.questions || []
  const [answers, setAnswers] = useState<Record<number, any>>({})
  const [checked, setChecked] = useState<Record<number, boolean>>({})
  const [saved, setSaved] = useState(false)

  const setAns = (qi: number, v: any) => {
    if (checked[qi]) return // đã kiểm tra câu này → không đổi đáp án nữa
    setAnswers((a) => ({ ...a, [qi]: v }))
  }

  const isCorrect = (q: any, given: any): boolean => {
    if (given === undefined || given === null || given === '') return false
    if (q.type === 'multiple_choice') return Number(given) === Number(q.correct)
    if (q.type === 'true_false') return given === q.correct
    if (q.type === 'fill_blank') {
      const cands = [q.answer, ...(q.alternatives || [])].map(norm).filter(Boolean)
      return cands.includes(norm(given))
    }
    return false
  }

  const allChecked = questions.length > 0 && questions.every((_: any, qi: number) => checked[qi])
  const correctCount = questions.reduce((n: number, q: any, qi: number) => n + (checked[qi] && isCorrect(q, answers[qi]) ? 1 : 0), 0)
  const score = questions.reduce((s: number, q: any, qi: number) => s + (checked[qi] && isCorrect(q, answers[qi]) ? (q.points || 1) : 0), 0)
  const maxScore = questions.reduce((s: number, q: any) => s + (q.points || 1), 0)

  // Kiểm tra 1 câu: chấm ngay + hiệu ứng; nếu là câu cuối cùng → lưu kết quả
  const handleCheck = (qi: number, ev: React.MouseEvent<HTMLButtonElement>) => {
    if (checked[qi] || answers[qi] === undefined || answers[qi] === null || answers[qi] === '') return
    const ok = isCorrect(questions[qi], answers[qi])
    if (ok) celebrateCorrect(ev.currentTarget)
    // Ghi vào thống kê tương tác chung của bài (LessonStatsService fallback currentLessonId)
    if (ok) LessonStatsService.recordCorrectAnswer()
    else LessonStatsService.recordWrongAnswer()
    const nextChecked = { ...checked, [qi]: true }
    setChecked(nextChecked)

    // Tất cả câu đã kiểm tra → tổng kết + lưu kết quả (1 lần)
    const done = questions.every((_: any, i: number) => nextChecked[i])
    if (done && !saved) {
      setSaved(true)
      let sc = 0, mx = 0, cor = 0
      const detail = questions.map((q: any, i: number) => {
        const pts = q.points || 1
        mx += pts
        const okI = isCorrect(q, answers[i])
        if (okI) { sc += pts; cor += 1 }
        return { qIndex: i, given: answers[i] ?? null, isCorrect: okI }
      })
      // Chỉ gửi kết quả riêng khi biết lessonId (render trong rich content thì kết quả
      // đã đi theo submitResults tổng hợp của LessonViewerPage qua LessonStatsService).
      if (lessonId) {
        resultApi.submit(lessonId, {
          card_id: card.id,
          lesson_slug: lessonSlug,
          score: sc,
          max_score: mx,
          total: questions.length,
          correct: cor,
          detail,
        })
      }
    }
  }

  if (questions.length === 0) {
    return <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">Chưa có câu hỏi.</div>
  }

  return (
    <div className="rounded-xl bg-blue-50/40 border border-blue-100 p-4">
      <h4 className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">🎯 {card.title || 'Câu hỏi kiểm tra'}</h4>

      <div className="space-y-4">
        {questions.map((q: any, qi: number) => {
          const given = answers[qi]
          const done = !!checked[qi]
          const ok = done && isCorrect(q, given)
          const wrong = done && !ok
          return (
            <div
              key={qi}
              className={`bg-white rounded-lg border p-4 ${done ? (ok ? 'border-green-300' : 'border-red-200') : 'border-blue-100'}`}
            >
              <div className="flex items-start gap-2 mb-2">
                <span className="text-sm font-bold text-blue-700 shrink-0">Câu {qi + 1}.</span>
                <div className="flex-1 text-gray-800 text-sm">
                  <KaTeXRenderer content={q.question || ''} />
                </div>
                {done && (ok ? <CheckCircle2 size={18} className="text-green-600 shrink-0" /> : <XCircle size={18} className="text-red-500 shrink-0" />)}
              </div>

              {/* Trắc nghiệm */}
              {q.type === 'multiple_choice' && (
                <div className="space-y-1.5">
                  {(q.options || []).map((opt: string, oi: number) => {
                    const sel = Number(given) === oi
                    const isAns = done && Number(q.correct) === oi
                    return (
                      <label
                        key={oi}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer text-sm ${
                          isAns ? 'bg-green-50' : sel ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input type="radio" name={`q-${card.id}-${qi}`} disabled={done} checked={sel} onChange={() => setAns(qi, oi)} />
                        <span className="text-gray-700"><KaTeXRenderer content={opt} /></span>
                        {isAns && <span className="text-green-600 text-xs font-bold">✓ đáp án</span>}
                      </label>
                    )
                  })}
                </div>
              )}

              {/* Đúng/Sai */}
              {q.type === 'true_false' && (
                <div className="flex gap-3">
                  {[true, false].map((val) => (
                    <label key={String(val)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer text-sm ${given === val ? 'bg-blue-50' : 'hover:bg-gray-50'} ${done && q.correct === val ? 'bg-green-50' : ''}`}>
                      <input type="radio" name={`q-${card.id}-${qi}`} disabled={done} checked={given === val} onChange={() => setAns(qi, val)} />
                      {val ? 'Đúng' : 'Sai'}
                    </label>
                  ))}
                </div>
              )}

              {/* Trả lời ngắn */}
              {q.type === 'fill_blank' && (
                <input
                  type="text"
                  disabled={done}
                  value={given || ''}
                  onChange={(e) => setAns(qi, e.target.value)}
                  placeholder="Nhập câu trả lời..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              )}

              {/* Nút Kiểm tra đáp án — chấm ngay câu này */}
              {!done && (
                <button
                  onClick={(e) => handleCheck(qi, e)}
                  disabled={given === undefined || given === null || given === ''}
                  className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <CheckCheck size={14} /> Kiểm tra đáp án
                </button>
              )}

              {/* Kết quả Đúng/Sai + giải thích ngay sau khi kiểm tra */}
              {done && (
                <div className={`mt-2 text-xs rounded-lg p-2 border ${ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
                  <p className="font-bold mb-1">{ok ? '🎉 Chính xác! Tuyệt vời!' : WRONG_ENCOURAGE}</p>
                  {q.type === 'fill_blank' && q.answer && wrong && <p>Đáp án: <strong>{q.answer}</strong></p>}
                  {q.explanation && <span className="text-cyan-900"><KaTeXRenderer content={'💡 ' + q.explanation} /></span>}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Tổng kết khi đã kiểm tra hết các câu (kết quả tự lưu, không cần Nộp bài) */}
      {allChecked && (
        <div className="mt-4 p-3 rounded-xl bg-white border border-blue-200 text-center">
          <p className="text-lg font-black text-blue-900">
            Điểm: {score.toFixed(2)} / {maxScore.toFixed(2)}
          </p>
          <p className="text-sm text-gray-500">Đúng {correctCount}/{questions.length} câu · Kết quả đã được ghi nhận</p>
        </div>
      )}
    </div>
  )
}

export default QuizPlayer
